import { useEffect, useSyncExternalStore } from 'react';

type Politeness = 'polite' | 'assertive';
interface Announcement {
  id: number;
  text: string;
  politeness: Politeness;
}

let current: Announcement | null = null;
let counter = 0;
const listeners = new Set<() => void>();

/** Announce text to screen readers via the mounted <LiveRegion />. */
export function announce(text: string, politeness: Politeness = 'polite') {
  counter += 1;
  current = { id: counter, text, politeness };
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Visually hidden aria-live regions. Mount once per screen that reports async state
 * (search results, sync status, voice commands).
 */
export function LiveRegion() {
  const item = useSyncExternalStore(
    subscribe,
    () => current,
    () => null,
  );
  useEffect(() => {
    if (!item) return;
    const handle = setTimeout(() => {
      if (current?.id === item.id) {
        current = null;
        listeners.forEach((l) => l());
      }
    }, 4000);
    return () => clearTimeout(handle);
  }, [item]);
  return (
    <>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {item?.politeness === 'polite' ? item.text : ''}
      </div>
      <div aria-live="assertive" aria-atomic="true" className="sr-only">
        {item?.politeness === 'assertive' ? item.text : ''}
      </div>
    </>
  );
}
